import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { SignatureCanvas } from './SignatureCanvas';
import type { RetiradorData, RetiradorErrors } from '../../types';

interface RetiradorFormProps {
  retirador: RetiradorData;
  onChange: (retirador: RetiradorData) => void;
  errors?: RetiradorErrors;
  maxCopias: number;
  onRemove?: () => void;
  showRemoveButton?: boolean;
  disabled?: boolean;
  index?: number;
}

export const RetiradorForm: React.FC<RetiradorFormProps> = ({
  retirador,
  onChange,
  errors = {},
  maxCopias,
  onRemove,
  showRemoveButton = false,
  disabled = false,
  index,
}) => {
  const handleChange = (field: keyof RetiradorData, value: any) => {
    onChange({
      ...retirador,
      [field]: value,
    });
  };

  return (
    <div className="space-y-4 p-4 rounded-lg border border-gray-200 bg-white min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900">
          {typeof index === 'number' ? `Persona ${index + 1}` : 'Datos del Retirador'}
        </h4>

        {showRemoveButton && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={disabled}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Nombre Completo */}
      <div className="space-y-2">
        <Label htmlFor={`nombre-${index}`}>
          Nombre Completo
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Input
          id={`nombre-${index}`}
          type="text"
          value={retirador.nombreCompleto}
          onChange={(e) => handleChange('nombreCompleto', e.target.value)}
          disabled={disabled}
          placeholder="Nombre y apellido completo"
          className={errors.nombreCompleto ? 'border-red-300' : ''}
        />
        {errors.nombreCompleto && (
          <p className="text-sm text-red-600">{errors.nombreCompleto}</p>
        )}
      </div>

      {/* Cantidad de Copias */}
      <div className="space-y-2">
        <Label htmlFor={`copias-${index}`}>
          Cantidad de Copias
          <span className="text-red-500 ml-1">*</span>
        </Label>
        <Select
          value={String(retirador.cantidadCopias)}
          onValueChange={(value) => handleChange('cantidadCopias', Number.parseInt(value, 10))}
          disabled={disabled}
        >
          <SelectTrigger
            id={`copias-${index}`}
            className={errors.cantidadCopias ? 'border-red-300' : ''}
          >
            <SelectValue placeholder="Seleccione cantidad" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: maxCopias }, (_, i) => i + 1).map((num) => (
              <SelectItem key={num} value={String(num)}>
                {num} {num === 1 ? 'copia' : 'copias'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.cantidadCopias && (
          <p className="text-sm text-red-600">{errors.cantidadCopias}</p>
        )}
      </div>

      {/* Firma Digital */}
      <SignatureCanvas
        onSave={(blob) => handleChange('firmaDigital', blob)}
        disabled={disabled}
        error={errors.firmaDigital}
        label="Firma Digital"
        required={false}
      />

      {retirador.firmaDigital && (
        <div className="flex items-center gap-2 text-sm text-green-600">
          <span>✓ Firma capturada correctamente</span>
        </div>
      )}
    </div>
  );
};
